<template>
  <section class="cw-container cw-mx-auto cw-px-4 lg:cw-p-0 cw-max-w-full">
    <div v-if="error"
      class="cw-flex cw-gap-x-2 cw-mt-10 cw-p-2 cw-border cw-border-rose-300 cw-rounded cw-bg-rose-50 cw-text-rose-700">
      <span class="cw-block fa-solid fa-circle-xmark !cw-leading-normal" />
      <p>{{ error }}</p>
    </div>
    <location-latency-filters-skeleton v-else-if="isLoading" />
    <template v-else>
      <!-- Filters -->
      <div class="cw-pt-10">
        <div class="cw-grid cw-grid-cols-2 cw-gap-2">
          <!-- Country selector -->
          <div class="cw-w-full"
            role="group">
            <label for="country-dropdown"
              class="cw-text-sm">Country</label>
            <select id="country-dropdown"
              class="form-select cw-block cw-appearance-none cw-w-full cw-py-1 cw-px-2 cw-text-base cw-leading-normal cw-bg-white cw-text-gray-800 cw-border cw-border-gray-200 cw-rounded"
              @change="handleFilterByCountry">
              <option value="allCountries">
                All Countries
              </option>
              <option v-for="country in locationsByCountry"
                :key="country"
                :value="country"
                :selected="country === filterCountryValue">
                {{ country }}
              </option>
            </select>
          </div>

          <!-- Data centre selector -->
          <div class="cw-w-full"
            role="group">
            <label for="data-centre-dropdown"
              class="cw-text-sm">Data Centre</label>
            <select id="data-centre-dropdown"
              class="form-select cw-block cw-appearance-none cw-w-full cw-py-1 cw-px-2 cw-text-base cw-leading-normal cw-bg-white cw-text-gray-800 cw-border cw-border-gray-200 cw-rounded"
              @change="handleSelectDataCentre">
              <option :value="null"
                :selected="selectedLocationId === null"
                disabled>
                Please Select a Data Centre
              </option>
              <option v-for="location in locationsFilteredByCountry"
                :key="location.id"
                :value="location.id"
                :selected="location.id === Number.parseInt(selectedLocationId)">
                {{ location.name }}
              </option>
            </select>
          </div>

          <!-- Text Filter -->
          <div class="cw-w-full">
            <label for="search-text"
              class="cw-sr-only">Search</label>
            <input id="search-text"
              v-model="filterText"
              type="search"
              class="cw-block cw-appearance-none cw-w-full cw-py-1 cw-px-2 cw-text-base cw-leading-normal cw-bg-white cw-text-gray-800 cw-border cw-border-gray-200 cw-rounded"
              :class="{ '!cw-bg-gray-100 cw-cursor-not-allowed': !selectedLocationId }"
              placeholder="Filter locations"
              :disabled="!selectedLocationId">
          </div>
        </div>
      </div>

      <!-- Location Name -->
      <h2 v-if="selectedLocationId"
        class="cw-text-5xl cw-my-4 cw-leading-tight">
        {{ locationNameByID[selectedLocationId] }}
      </h2>

      <!-- Table -->
      <div class="cw-mt-4 cw-flex cw-flex-wrap">
        <div class="cw-w-full">
          <div v-if="selectedLocationId === null"
            class="cw-flex cw-gap-x-2 cw-mt-5 cw-p-2 cw-border cw-border-gray-200 cw-rounded cw-bg-gray-50 cw-text-gray-600">
            <span class="cw-block fa-solid fa-circle-info !cw-leading-normal" />
            <p>Please select a data centre to view latency results.</p>
          </div>
          <div v-else-if="isLoadingLatency"
            class="cw-animate-pulse cw-h-96 cw-w-full cw-bg-slate-300 cw-rounded" />
          <div v-else
            class="cw-max-h-96 cw-overflow-y-scroll cw-border-b">
            <table class="cw-w-full cw-text-xl cw-border-separate cw-border-spacing-0">
              <thead>
                <tr>
                  <th v-for="field in latencyFields"
                    :key="field.key"
                    class="cw-border-y-2 cw-font-normal cw-p-2 cw-sticky cw-top-0 cw-bg-white">
                    <div class="cw-flex cw-justify-between cw-items-center cw-pr-2">
                      <div class="cw-font-bold">
                        {{ field.label }}
                      </div>
                      <div class="cw-flex cw-flex-col cw-pl-2 cw-leading-none cw-text-base cw-cursor-pointer"
                        @click="changeSorting(field.key)">
                        <div class="cw-select-none"
                          :class="currentSortField === field.key && sortDirection === 1 ? 'cw-black' : 'cw-text-gray-200'">
                          ▲
                        </div>
                        <div class="cw-select-none"
                          :class="currentSortField === field.key && sortDirection === -1 ? 'cw-black' : 'cw-text-gray-200'">
                          ▼
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="location in latencyMap"
                  :key="location.id"
                  class="cw-w-full cw-font-normal">
                  <td v-for="field in latencyFields"
                    :key="field.key"
                    class="cw-p-2 cw-border-b">
                    {{ field.key === 'location' ? location.location : formatLatency(location[field.key]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { DateTime } from 'luxon'
import axios from 'axios'
import LocationLatencyFiltersSkeleton from '@/location-latency/components/LocationLatencyFiltersSkeleton.vue'
import { updateSearchParams } from '@/shared/helpers.js'

export default {
  name: 'NetworkLatency',

  components: {
    'location-latency-filters-skeleton': LocationLatencyFiltersSkeleton,
  },

  data() {
    return {
      isLoading: true,
      isLoadingLatency: false,
      locations: [],
      selectedCountry: null,
      filterCountryValue: 'allCountries',
      selectedLocationId: null,
      latencyList: [],
      error: null,
      sortDirection: 1, // 1 = ascending, -1 = descending
      filterText: '',
      showAdditionalMonths: false,
      currentSortField: 'location',
    }
  },

  computed: {
    latencyFields() {
      const fields = []

      fields.push({
        key: 'location',
        label: 'Location',
        sortable: true,
      })

      fields.push({
        key: 'oneMonthAgo',
        label: this.periodWindows.oneMonthAgo.toFormat('LLLL'),
        sortable: true,
      })

      if (this.showAdditionalMonths) {
        fields.push({
          key: 'twoMonthsAgo',
          label: this.periodWindows.twoMonthsAgo.toFormat('LLLL'),
          sortable: true,
        })

        fields.push({
          key: 'threeMonthsAgo',
          label: this.periodWindows.threeMonthsAgo.toFormat('LLLL'),
          sortable: true,
        })
      }

      return fields
    },
    isFirstThreeDays() {
      return DateTime.utc().day <= 3
    },
    periodWindows() {
      // Count back an additional three days if today is first three days of month
      const oneMonthAgo = DateTime.utc().minus({ months: 1, days: this.isFirstThreeDays ? 3 : 0 })
      const twoMonthsAgo = DateTime.utc().minus({ months: 2, days: this.isFirstThreeDays ? 3 : 0 })
      const threeMonthsAgo = DateTime.utc().minus({ months: 3, days: this.isFirstThreeDays ? 3 : 0 })

      return { oneMonthAgo, twoMonthsAgo, threeMonthsAgo }
    },
    latencyUrls() {
      const { oneMonthAgo, twoMonthsAgo, threeMonthsAgo } = this.periodWindows

      return [
        `${process.env.VUE_APP_API}/rtt?year=${oneMonthAgo.toFormat('yy')}&month=${oneMonthAgo.month}`,
        `${process.env.VUE_APP_API}/rtt?year=${twoMonthsAgo.toFormat('yy')}&month=${twoMonthsAgo.month}`,
        `${process.env.VUE_APP_API}/rtt?year=${threeMonthsAgo.toFormat('yy')}&month=${threeMonthsAgo.month}`,
      ]
    },
    locationsByCountry() {
      const countries = new Set()

      this.locations.forEach(location => countries.add(location.country))

      return Array.from(countries).sort()
    },
    locationsFilteredByCountry() {
      if (this.filterCountryValue === 'allCountries') return this.locations

      return this.locations.filter(location => location.country.toLowerCase() === this.filterCountryValue.toLowerCase())
    },
    locationNameByID() {
      return this.locations.reduce((locationsMap, location) => {
        locationsMap[location.id] = location.name
        return locationsMap
      }, {})
    },
    latencyMap() {
      // Build a location ID dictionary to store latency data, containing the location name and latency values for given time spans.
      const latencyMap = {}

      // The value of latencyList is an array of arrays. Each array contains latency details from the selected source location
      // to all other locations during a given time span. The data is structured in objects containing the source and destination location IDs
      // and the median RTT (Round Trip Time), namely `srcLocation`, `dstLocation`, `medianRTT`, respectively.
      this.latencyList.forEach((list, index) => {
        list.forEach(({ dstLocation, medianRTT }) => {
          const locationName = this.locationNameByID[dstLocation]

          if (!locationName) return

          if (!Object.hasOwn(latencyMap, dstLocation)) {
            latencyMap[dstLocation] = {
              id: dstLocation,
              location: locationName,
              oneMonthAgo: null,
              twoMonthsAgo: null,
              threeMonthsAgo: null,
            }
          }

          if (index === 0) latencyMap[dstLocation].oneMonthAgo = medianRTT
          if (index === 1) latencyMap[dstLocation].twoMonthsAgo = medianRTT
          if (index === 2) latencyMap[dstLocation].threeMonthsAgo = medianRTT
        })
      })

      const latencyArray = Object.values(latencyMap)

      const filteredArray = latencyArray.filter(({ location }) => {
        if (!this.filterText) return true

        return location.toLowerCase().includes(this.filterText.toLowerCase())
      })

      filteredArray.sort((a, b) => {
        const valueA = a[this.currentSortField]
        const valueB = b[this.currentSortField]

        // Always sort nullish values last, ignoring the sort direction
        if (valueA == null && valueB == null) return 0
        if (valueA == null) return 1
        if (valueB == null) return -1

        const comparison = typeof valueA === 'string' && typeof valueB === 'string'
          // Use localeCompare for case-insensitive string comparison
          ? valueA.localeCompare(valueB, undefined, { sensitivity: 'base' })
          : valueA - valueB

        return comparison * this.sortDirection
      })

      return filteredArray
    },
  },

  async created() {
    // Get a list of locations
    const status = ['Active']
    const locationsUrl = `${process.env.VUE_APP_API}?locationStatuses${status.join('&locationStatuses=')}`

    try {
      const response = await axios(locationsUrl)

      this.locations = response.data.data
    } catch {
      this.error = 'Could not connect with server. Please try again later.'
    }

    this.isLoading = false
  },

  mounted() {
    this.handleUrlParams()
  },

  methods: {
    handleUrlParams() {
      const urlParams = new URLSearchParams(window.location.search)

      this.showAdditionalMonths = urlParams.has('supersizeme') ? true : false
      this.filterCountryValue = urlParams.get('country') || 'allCountries'

      const locationId = urlParams.get('locationId') || null

      if (locationId) {
        this.selectedLocationId = locationId
        this.fetchLatencyData()
      }
    },

    handleFilterByCountry(event) {
      this.filterCountryValue = event.target.value
      updateSearchParams({ country: this.filterCountryValue })
    },

    handleSelectDataCentre(event) {
      this.selectedLocationId = event.target.value
      updateSearchParams({ country: this.filterCountryValue, locationId: this.selectedLocationId })
      this.fetchLatencyData()
    },

    async fetchLatencyData() {
      this.error = null
      this.isLoadingLatency = true

      // Only fetch additional months if they are meant to be shown
      if (this.showAdditionalMonths) {
        const urls = this.latencyUrls.map(async url => {
          const response = await axios(`${url}&srcLocation=${this.selectedLocationId}`)
          return response.data.data
        })

        Promise.all(urls).then(res => {
          this.latencyList = res
        })
      } else {
        const response = await axios(`${this.latencyUrls[0]}&srcLocation=${this.selectedLocationId}`)
        this.latencyList = [response.data.data]
      }

      this.isLoadingLatency = false
    },

    formatLatency(latency) {
      return latency == null ? '-' : `${latency.toFixed(2)} ms`
    },

    changeSorting(field) {
      if (this.currentSortField === field) {
        this.sortDirection *= -1
      } else {
        this.currentSortField = field
        this.sortDirection = 1
      }
    },
  },
}
</script>

<style scoped>
.form-select {
    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAQCAYAAAAMJL+VAAAABGdBTUEAALGPC/xhBQAAAQtJREFUOBG1lEEOgjAQRalbGj2OG9caOACn4ALGtfEuHACiazceR1PWOH/CNA3aMiTaBDpt/7zPdBKy7M/DCL9pGkvxxVp7KsvyJftL5rZt1865M+Ucq6pyyF3hNcI7Cuu+728QYn/JQA5yKaempxuZmQngOwEaYx55nu+1lQh8GIatMGi+01NwBcEmhxBqK4nAPZJ78K0KKFAJmR3oPp8+Iwgob0Oa6+TLoeCvRx+mTUYf/FVBGTPRwDkfLxnaSrRwcH0FWhNOmrkWYbE2XEicqgSa1J0LQ+aPCuQgZiLnwewbGuz5MGoAhcIkCQcjaTBjMgtXGURMVHC1wcQEy0J+Zlj8bKAnY1/UzDe2dbAVqfXn6wAAAABJRU5ErkJggg==');
    background-size: 0.7rem;
    background-position: right 0.7rem center;
    background-repeat: no-repeat;
  }
</style>
